import { Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React, { useState } from "react";
import { isAuthenticated } from "../../../commons/Auth/auth";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import DragUpload from "../../../commons/DragUpload/DragUpload";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { useTranslation } from "react-i18next";

dayjs.extend(customParseFormat);

const DOB_FORMAT = "DD-MM-YYYY";

// Older saves stored the DatePicker value as an ISO string instead of DD-MM-YYYY.
const parseDob = dob => {
  if (!dob) return null;
  const strict = dayjs(dob, DOB_FORMAT, true);
  if (strict.isValid()) return strict;
  const loose = dayjs(dob);
  return loose.isValid() ? loose : null;
};

const UpdateProfile = ({ data, updateParent }) => {
  const { t } = useTranslation("dashboard");
  const [loading, setLoading] = useState(false);
  const [picture, setPicture] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isEmployee = isAuthenticated()?.role === 1;

  const showModal = () => {
    setPicture(null);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onFinish = async formValues => {
    setLoading(true);
    const values = { ...formValues };
    if (!isEmployee) {
      values.dob = formValues.dob ? formValues.dob.format(DOB_FORMAT) : "";
    }
    if (picture) values.picture = picture;
    try {
      const res = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/users/me`, values, {
        headers: { Authorization: "Bearer " + localStorage.getItem("token") }
      });
      // Keep the stored login object (header name, avatar) in step with the new data.
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...stored, ...res.data.user }));
      Success(t("Profile updated successfully!"));
      updateParent();
      handleCancel();
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  const required = field => [{ required: true, whitespace: true, message: t("enter", { field }) }];

  const initialValues = {
    ...data,
    dob: parseDob(data?.dob),
    iban: data?.bankDetails?.iban
  };

  return (
    <>
      <MainButtonComp text="Edit" editBtn onClick={showModal} />
      <Modal
        destroyOnClose
        title={t("Edit Profile")}
        open={isModalOpen}
        onCancel={handleCancel}
        width={720}
        footer={
          // Submits the form below through the HTML form attribute (Enter still works).
          <Button
            loading={loading}
            type="primary"
            block
            htmlType="submit"
            form="update-profile-form"
          >
            {t("Submit")}
          </Button>
        }
      >
        <Form
          id="update-profile-form"
          size="large"
          initialValues={initialValues}
          layout="vertical"
          onFinish={onFinish}
        >
          <Row gutter={[10, 0]}>
            <Col xs={24} className="mb-4">
              <DragUpload
                noMultiple
                accept="image"
                value={picture ? [picture] : undefined}
                updateFiles={val => setPicture(val?.[0] || null)}
              />
            </Col>
            {!isEmployee && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("First Name")}
                    name="firstName"
                    rules={required(t("First Name"))}
                  >
                    <Input autoComplete="given-name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Last Name")} name="lastName">
                    <Input autoComplete="family-name" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t("Email")}
                    name="email"
                    rules={[
                      ...required(t("Email")),
                      { type: "email", message: t("Please enter a valid email address.") }
                    ]}
                  >
                    <Input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="none"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Date of Birth")} name="dob">
                    <DatePicker
                      format={DOB_FORMAT}
                      disabledDate={current => current && current > dayjs().endOf("day")}
                      className="w-100"
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} md={12}>
              <Form.Item label={t("Phone")} name="phone">
                <Input type="tel" inputMode="tel" autoComplete="tel" />
              </Form.Item>
            </Col>
            {!isEmployee && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item label={t("Tax Number")} name="taxNumber">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label={t("IBAN")} name="iban">
                    <Input autoCapitalize="characters" autoComplete="off" spellCheck={false} />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24}>
              <Form.Item label={t("Description")} name="description">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <h5 className="form-title">
                <span>{t("Address")}</span>
              </h5>
            </Col>
            {isEmployee ? (
              <>
                <Col xs={16} md={16}>
                  <Form.Item label={t("Street")} name="street">
                    <Input autoComplete="address-line1" />
                  </Form.Item>
                </Col>
                <Col xs={8} md={8}>
                  <Form.Item label={t("House Number")} name="streetNumber">
                    <InputNumber min={0} className="w-100" inputMode="numeric" />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <Col xs={24}>
                <Form.Item label={t("Address")} name="address">
                  <Input autoComplete="street-address" />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item label={t("Zip Code")} name="zipCode">
                <Input autoComplete="postal-code" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("City")} name="city">
                <Input autoComplete="address-level2" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("State")} name="state">
                <Input autoComplete="address-level1" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("Country")} name="country">
                <Input autoComplete="country-name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateProfile;
